import React from 'react';
import { compose } from 'redux';
import oneAppModuleWrapper, {
  OneAppModuleWrapperProps,
} from '@americanexpress/one-app-module-wrapper';
import { composeModules, LoadModuleData, isLoaded } from 'holocron';
import { IntlProvider } from 'react-intl';
import { useSelector } from 'react-redux';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import { combineReducers } from 'redux-immutable';
import { OneFetchyeProvider, OneCache } from 'fetchye-one-app';
import { Helmet } from 'react-helmet';
import type { OneAppV6RootModule } from 'one-app-types';
import SharedScript from '@americanexpress/shared-scripts';
import childRoutes from '../childRoutes';
import authenticatedReducer from '../ducks/authenticated';
import Footer, { AxpFooterModule } from './Footer';
import useHeapAnalytics from '../hooks/useHeapAnalytics';

type Props = {
  children: React.ReactNode;
} & OneAppModuleWrapperProps;
const OneAP: OneAppV6RootModule<Props, typeof authenticatedReducer> = ({
  languageData,
  locale,
  children,
}) => {
  const isAxpFooterLoaded = useSelector(isLoaded(AxpFooterModule.name));
  useHeapAnalytics();
  return (
    <IntlProvider locale={locale} messages={languageData}>
      <Helmet>
        <title>One AP | American Express</title>
        <html lang={locale} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="application-name" content="American Express" />
        <link
          rel="stylesheet"
          href="https://www.aexp-static.com/cdaas/dls/packages/@americanexpress/dls/7.4.0/stylesheets/dls-max.min.css"
        />
        <link rel="icon" href="https://www.americanexpress.com/favicon.ico" />
      </Helmet>
      <SharedScript
        name="one-identity-session"
        version="^1.0.0"
        windowCheck="AmexSession"
      />
      <OneFetchyeProvider>
        {children}
        {isAxpFooterLoaded && <Footer />}
      </OneFetchyeProvider>
    </IntlProvider>
  );
};

export const TestableOneAP = OneAP;

OneAP.childRoutes = childRoutes;

/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line @typescript-eslint/no-require-imports,global-require -- require needs to be inside browser check
  OneAP.appConfig = require('../appConfig').default;
}

export const loadModuleData: LoadModuleData<Props> = ({
  store: { dispatch },
}) =>
  Promise.all([
    dispatch(loadLanguagePack('one-ap-root', { fallbackLocale: 'en-US' })),
    dispatch(composeModules([{ name: 'axp-footer' }])),
  ]);

OneAP.holocron = {
  name: 'one-ap-root',
  loadModuleData,
  reducer: combineReducers({
    fetchye: OneCache().reducer,
    authenticated: authenticatedReducer,
  }),
};
const hocChain = compose(oneAppModuleWrapper('one-ap-root'));

export default hocChain(OneAP);
